/**
 * AliExpress Products Page
 *
 * Browse products by category with sidebar navigation
 * Includes PC build/quote functionality
 * Categories are loaded dynamically from the database
 */

import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Search,
  Package,
  TrendingUp,
  ChevronDown,
  ShoppingCart,
  Plus,
  Check,
  X,
  Calculator
} from 'lucide-react';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { CategoryIcon } from '../components/common/CategoryIcon';
import { estimateKrwPrice } from '../lib/aliexpress';
import { useSidebarCategories, useBuildCategories } from '../hooks/useAliExpressCategories';
import type { AliExpressHotProduct } from '../types';

interface SearchProduct extends AliExpressHotProduct {
  shopName?: string;
  evaluateScore?: number;
}

interface BuildItem {
  category: string;
  categoryName: string;
  product: SearchProduct | null;
}

export function AliExpressProductsPage() {
  const { categories, isLoading: loadingCategories } = useSidebarCategories();
  const buildCategories = useBuildCategories();

  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<SearchProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // PC Build State
  const [showBuildPanel, setShowBuildPanel] = useState(false);
  const [buildItems, setBuildItems] = useState<BuildItem[]>([]);

  // Initialize build items when build categories are loaded
  useEffect(() => {
    if (buildCategories.length > 0 && buildItems.length === 0) {
      setBuildItems(buildCategories.map(cat => ({
        category: cat.id,
        categoryName: cat.name,
        product: null
      })));
    }
  }, [buildCategories, buildItems.length]);

  const source = searchParams.get('source');
  const categoryParam = searchParams.get('category');

  useEffect(() => {
    const query = searchParams.get('q') || searchParams.get('search') || '';
    if (query) {
      setSearchQuery(query);
      setInputValue(query);
      setSelectedCategory('all');
    } else if (categoryParam) {
      setSelectedCategory(categoryParam);
      const cat = categories.find(c => c.id === categoryParam);
      if (cat) {
        // Use keywords if available, otherwise use slug (English) for better API results
        setSearchQuery(cat.keywords || cat.id || cat.name);
        setInputValue('');
      }
    } else {
      // Default: show all products on first load
      setSearchQuery('computer PC gaming');
      setSelectedCategory('all');
    }
  }, [searchParams, source, categoryParam, categories]);

  useEffect(() => {
    async function fetchProducts() {
      if (!searchQuery) {
        setProducts([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`https://moapc-worker.linuxm.workers.dev/api/aliexpress/search?keywords=${encodeURIComponent(searchQuery)}&pageSize=50`);
        if (!response.ok) throw new Error('제품을 불러올 수 없습니다');
        const data = await response.json();
        setProducts(data.products || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : '제품을 불러올 수 없습니다');
      } finally {
        setLoading(false);
      }
    }

    fetchProducts();
  }, [searchQuery]);

  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategory(categoryId);
    if (categoryId === 'all') {
      setSearchQuery('computer PC');
      setSearchParams({});
    } else {
      const cat = categories.find(c => c.id === categoryId);
      if (cat) {
        // Use keywords if available, otherwise use slug (English) for better API results
        setSearchQuery(cat.keywords || cat.id || cat.name);
        setSearchParams({ category: categoryId });
      }
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim()) {
      setSearchQuery(inputValue.trim());
      setSelectedCategory('all');
      setSearchParams({ q: inputValue.trim() });
    }
  };

  // PC Build functions
  const addToBuild = (product: SearchProduct, category: string) => {
    setBuildItems(prev => prev.map(item =>
      item.category === category ? { ...item, product } : item
    ));
    setShowBuildPanel(true);
  };

  const removeFromBuild = (category: string) => {
    setBuildItems(prev => prev.map(item =>
      item.category === category ? { ...item, product: null } : item
    ));
  };

  const getBuildTotal = () => {
    return buildItems.reduce((total, item) => {
      if (item.product) {
        return total + estimateKrwPrice(item.product.salePrice);
      }
      return total;
    }, 0);
  };

  const isProductInBuild = (productId: string) => {
    return buildItems.some(item => item.product?.productId === productId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container py-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                제품 검색
              </h1>
              <p className="text-sm text-gray-600">
                알리익스프레스에서 저렴한 컴퓨터 부품을 찾아보세요
              </p>
            </div>

            {/* PC Build Button */}
            <button
              onClick={() => setShowBuildPanel(!showBuildPanel)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                showBuildPanel
                  ? 'bg-orange-600 text-white'
                  : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
              }`}
            >
              <Calculator size={18} />
              PC 견적 ({buildItems.filter(i => i.product).length}/{buildCategories.length})
            </button>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mt-4">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="제품명을 입력하세요 (예: RTX 4090, Ryzen 9)"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <button
                type="submit"
                className="px-6 py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors"
              >
                검색
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-6">
        <div className="flex gap-6">
          {/* Sidebar - Categories */}
          <aside className={`${sidebarOpen ? 'w-56' : 'w-12'} flex-shrink-0 transition-all duration-300`}>
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="w-full flex items-center justify-between p-3 border-b hover:bg-gray-50"
              >
                {sidebarOpen && <span className="font-medium text-gray-900">카테고리</span>}
                <ChevronDown
                  size={18}
                  className={`text-gray-500 transition-transform ${!sidebarOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {sidebarOpen && (
                <nav className="py-2">
                  {loadingCategories ? (
                    <div className="px-4 py-2">
                      <LoadingSpinner size="sm" />
                    </div>
                  ) : (
                    categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleCategoryClick(cat.id)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                          selectedCategory === cat.id
                            ? 'bg-orange-50 text-orange-700 border-r-2 border-orange-500'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className={selectedCategory === cat.id ? 'text-orange-500' : 'text-gray-400'}>
                          <CategoryIcon iconName={cat.icon} size={20} />
                        </span>
                        <span className="text-sm font-medium">{cat.name}</span>
                      </button>
                    ))
                  )}
                </nav>
              )}
            </div>

            {/* Build Summary (Mobile) */}
            {!sidebarOpen && (
              <button
                onClick={() => setShowBuildPanel(true)}
                className="mt-4 w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600 hover:bg-orange-200 transition-colors"
              >
                <ShoppingCart size={20} />
              </button>
            )}
          </aside>

          {/* Products Grid */}
          <main className="flex-1 min-w-0">
            {/* Results Info */}
            {!loading && searchQuery && (
              <div className="mb-4 text-gray-600 text-sm">
                "{searchQuery}" 검색 결과: {products.length}개
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
                {error}
              </div>
            )}

            {/* Loading */}
            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map((product) => (
                  <ProductCard
                    key={product.productId}
                    product={product}
                    selectedCategory={selectedCategory}
                    onAddToBuild={addToBuild}
                    isInBuild={isProductInBuild(product.productId)}
                    buildCategories={buildCategories}
                  />
                ))}
              </div>
            ) : searchQuery ? (
              <div className="text-center py-12 bg-white rounded-xl border">
                <Package size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">검색 결과가 없습니다.</p>
                <p className="text-sm text-gray-400 mt-2">다른 키워드로 검색해보세요.</p>
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl border">
                <Search size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">카테고리를 선택하거나 검색어를 입력하세요.</p>
              </div>
            )}
          </main>

          {/* Build Panel */}
          {showBuildPanel && (
            <aside className="w-72 flex-shrink-0">
              <div className="bg-white rounded-xl border shadow-sm overflow-hidden sticky top-4">
                <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold flex items-center gap-2">
                      <Calculator size={18} />
                      PC 견적
                    </h3>
                    <button
                      onClick={() => setShowBuildPanel(false)}
                      className="p-1 hover:bg-white/20 rounded"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <div className="mt-3">
                    <span className="text-sm opacity-90">총 견적 금액</span>
                    <p className="text-2xl font-bold">₩{getBuildTotal().toLocaleString()}</p>
                  </div>
                </div>

                <div className="p-4 space-y-3">
                  {buildItems.map((item) => (
                    <div key={item.category} className="border-b pb-3 last:border-0">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {item.categoryName}
                          {buildCategories.find(c => c.id === item.category)?.required && (
                            <span className="text-red-500 ml-1">*</span>
                          )}
                        </span>
                        {item.product && (
                          <button
                            onClick={() => removeFromBuild(item.category)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                      {item.product ? (
                        <div className="flex gap-2 items-center">
                          <img
                            src={item.product.imageUrl}
                            alt={item.product.productTitle}
                            className="w-10 h-10 rounded object-cover"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-900 truncate">
                              {item.product.productTitle}
                            </p>
                            <p className="text-sm font-bold text-orange-600">
                              ₩{estimateKrwPrice(item.product.salePrice).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400">선택 안됨</p>
                      )}
                    </div>
                  ))}
                </div>

                {getBuildTotal() > 0 && (
                  <div className="p-4 border-t bg-gray-50">
                    <Link
                      to="/compare"
                      className="w-full flex items-center justify-center gap-2 py-2.5 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors"
                    >
                      <ShoppingCart size={18} />
                      견적 저장/비교
                    </Link>
                  </div>
                )}
              </div>
            </aside>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div className="container pb-8">
        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
          <p className="text-sm text-gray-600">
            <strong>안내:</strong> 모든 제품은 알리익스프레스 제휴 링크를 통해 연결됩니다.
            한국으로 직배송 가능한 제품들을 확인하시고, 관부가세는 별도 부과될 수 있습니다.
          </p>
        </div>
      </div>
    </div>
  );
}

interface ProductCardProps {
  product: SearchProduct;
  selectedCategory: string;
  onAddToBuild: (product: SearchProduct, category: string) => void;
  isInBuild: boolean;
  buildCategories: { id: string; name: string; required: boolean }[];
}

function ProductCard({ product, selectedCategory, onAddToBuild, isInBuild, buildCategories }: ProductCardProps) {
  const krwPrice = estimateKrwPrice(product.salePrice);
  const originalKrwPrice = product.originalPrice > product.salePrice
    ? estimateKrwPrice(product.originalPrice)
    : null;

  const [showCategorySelect, setShowCategorySelect] = useState(false);

  const handleAddClick = () => {
    if (selectedCategory !== 'all' && buildCategories.some(c => c.id === selectedCategory)) {
      onAddToBuild(product, selectedCategory);
    } else {
      setShowCategorySelect(true);
    }
  };

  const placeholderImage = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300"><rect fill="#f3f4f6" width="300" height="300"/><text fill="#9ca3af" font-family="Arial" font-size="14" x="50%" y="50%" text-anchor="middle" dominant-baseline="middle">Product</text></svg>')}`;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg hover:border-orange-300 transition-all group">
      {/* Image */}
      <Link
        to={`/products/aliexpress/${product.productId}`}
        state={{ product }}
        className="block aspect-square bg-gray-100 relative overflow-hidden"
      >
        <img
          src={product.imageUrl || placeholderImage}
          alt={product.productTitle}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          loading="lazy"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (!target.src.startsWith('data:image')) {
              target.src = placeholderImage;
            }
          }}
        />
        {originalKrwPrice && (
          <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
            {Math.round((1 - product.salePrice / product.originalPrice) * 100)}% OFF
          </div>
        )}
      </Link>

      {/* Info */}
      <div className="p-3">
        <Link
          to={`/products/aliexpress/${product.productId}`}
          state={{ product }}
          className="block"
        >
          <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-2 min-h-[40px] group-hover:text-orange-600 transition-colors">
            {product.productTitle}
          </h3>
        </Link>

        {product.shopName && (
          <p className="text-xs text-gray-500 mb-2 truncate">{product.shopName}</p>
        )}

        {product.volume > 0 && (
          <div className="flex items-center gap-1 mb-2">
            <TrendingUp size={12} className="text-green-500" />
            <span className="text-xs text-gray-500">
              {product.volume.toLocaleString()}개 판매
            </span>
          </div>
        )}

        <div className="flex items-baseline gap-2">
          <span className="text-lg font-bold text-orange-600">
            ₩{krwPrice.toLocaleString()}
          </span>
          {originalKrwPrice && (
            <span className="text-xs text-gray-400 line-through">
              ₩{originalKrwPrice.toLocaleString()}
            </span>
          )}
        </div>

        {/* Add to Build Button */}
        <div className="mt-3 relative">
          {showCategorySelect ? (
            <div className="space-y-1">
              {buildCategories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => {
                    onAddToBuild(product, cat.id);
                    setShowCategorySelect(false);
                  }}
                  className="w-full text-left px-2 py-1.5 text-xs bg-gray-50 hover:bg-orange-50 rounded border border-gray-200"
                >
                  {cat.name}에 추가
                </button>
              ))}
              <button
                onClick={() => setShowCategorySelect(false)}
                className="w-full text-center py-1 text-xs text-gray-500"
              >
                취소
              </button>
            </div>
          ) : isInBuild ? (
            <div className="flex items-center justify-center gap-1 py-2 bg-green-100 text-green-700 rounded text-sm font-medium">
              <Check size={16} />
              견적에 추가됨
            </div>
          ) : (
            <button
              onClick={handleAddClick}
              className="w-full flex items-center justify-center gap-1 py-2 bg-orange-100 text-orange-700 rounded text-sm font-medium hover:bg-orange-200 transition-colors"
            >
              <Plus size={16} />
              견적에 추가
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AliExpressProductsPage;
